/**
 * SimulationKernel — 确定性模拟内核。
 * 固定 Tick、种子 RNG、稳定 ID、System Pipeline、Command Queue、
 * Domain Event Buffer、状态哈希与不变量框架。
 * 提供创建空 World、推进 Tick、导出/导入纯数据快照。
 */

import type {
  CommandResult,
  GameCommand,
} from '@crater-zero/shared-types';

import { CommandQueue } from './command-queue.js';
import { SimulationContext, type RecordedEvent } from './context.js';
import { DomainEventBuffer } from './event-buffer.js';
import { IdAllocator } from './id-allocator.js';
import { InvariantRegistry, type InvariantViolation } from './invariants.js';
import { SeededRng } from './rng.js';
import { type SpatialMapConfig } from './spatial/types.js';
import { generateMap } from './spatial/grid.js';
import { createEmptyPowerState } from './power/power.js';
import { createEmptyFluidState } from './utility/fluid.js';
import { createEmptyProductionState } from './production/production.js';
import { createEmptyPopulationState } from './population/population.js';
import { createEmptyConstructionState } from './building/construction.js';
import { hashWorldState } from './state-hash.js';
import type { CommandHandler, SimulationSystem } from './system.js';
import type { WorldState } from './world-state.js';

/** 每个 Tick 推进的游戏分钟数（固定步长）。 */
export const MINUTES_PER_TICK = 1;
/** 一天的游戏分钟数。 */
export const MINUTES_PER_DAY = 1440;

export interface KernelOptions {
  readonly scenarioId: string;
  readonly seed: number;
  /** 可选地图配置；不给时用 DEFAULT_MAP_CONFIG（既有行为）。 */
  readonly mapConfig?: SpatialMapConfig;
  /**
   * 是否每 Tick 计算状态哈希并放进 TickOutcome（默认 false）。
   *
   * 全量哈希要遍历整张地图与全部实体，实测占空转 Tick 的约 3/4 —— 领域逻辑
   * 本身几乎不花钱。绝大多数调用方只在需要时显式调 `stateHash()`，不该为这个
   * 调试字段付每帧的税。确定性排查（定位两次运行从哪个 Tick 开始分叉）才需要
   * 逐 Tick 哈希，届时显式打开。
   */
  readonly hashEveryTick?: boolean;
}

export interface TickOutcome {
  readonly tick: number;
  readonly commandResults: readonly CommandResult[];
  readonly events: readonly RecordedEvent[];
  readonly violations: readonly InvariantViolation[];
  /** 逐 Tick 状态哈希；仅在 `hashEveryTick` 打开时非 null（见 ADR-0001）。 */
  readonly stateHash: string | null;
}

function emptyDomain(note: string): { readonly note: string } {
  return { note };
}

/** 创建一个空的初始 WorldState。 */
export function createEmptyWorld(options: KernelOptions): WorldState {
  return {
    meta: { scenarioId: options.scenarioId, createdWithSeed: options.seed },
    clock: { tick: 0, minuteOfDay: 0, day: 1 },
    rng: SeededRng.fromSeed(options.seed).snapshot(),
    ids: IdAllocator.create().snapshot(),
    entities: { order: [], kind: {}, components: {} },
    resources: { stock: {} },
    spatial: generateMap(options.mapConfig),
    construction: createEmptyConstructionState(),
    utilityNetworks: createEmptyPowerState(),
    fluidNetworks: createEmptyFluidState(),
    production: createEmptyProductionState(),
    population: createEmptyPopulationState(),
    research: emptyDomain('research'),
    policies: emptyDomain('policies'),
    events: emptyDomain('events'),
    expeditions: emptyDomain('expeditions'),
    scenario: emptyDomain('scenario'),
    alerts: emptyDomain('alerts'),
    metrics: emptyDomain('metrics'),
  };
}

export class SimulationKernel {
  private state: WorldState;
  private rng: SeededRng;
  private ids: IdAllocator;
  private readonly queue = new CommandQueue();
  private readonly eventBuffer = new DomainEventBuffer();
  private readonly systems: SimulationSystem[] = [];
  private readonly handlers = new Map<string, CommandHandler>();
  readonly invariants = new InvariantRegistry();

  private readonly hashEveryTick: boolean;

  private constructor(state: WorldState, hashEveryTick = false) {
    this.hashEveryTick = hashEveryTick;
    this.state = state;
    this.rng = SeededRng.fromState(state.rng);
    this.ids = IdAllocator.fromState(state.ids);
  }

  static create(options: KernelOptions): SimulationKernel {
    return new SimulationKernel(createEmptyWorld(options), options.hashEveryTick ?? false);
  }

  /** 从快照恢复（快照先深拷贝，之后与内核状态互不影响）。 */
  static fromSnapshot(snapshot: WorldState, hashEveryTick = false): SimulationKernel {
    return new SimulationKernel(structuredClone(snapshot), hashEveryTick);
  }

  /** 注册 System（按注册顺序进入 Pipeline，顺序即契约）。 */
  addSystem(system: SimulationSystem): void {
    this.systems.push(system);
  }

  /** 注册 Command 处理器。 */
  addCommandHandler(handler: CommandHandler): void {
    this.handlers.set(handler.type, handler);
  }

  /** 入队一个命令（下 Tick 消费）。 */
  submitCommand(command: GameCommand): boolean {
    return this.queue.enqueue(command);
  }

  /** 当前只读世界状态引用（投影/测试用；不得在外部持有并跨 Tick 修改）。 */
  get worldState(): Readonly<WorldState> {
    return this.state;
  }

  /** 导出纯数据快照（深拷贝，之后不被 World 修改）。 */
  exportSnapshot(): WorldState {
    return structuredClone(this.state);
  }

  /** 当前状态哈希。 */
  stateHash(): string {
    return hashWorldState(this.state);
  }

  /** 推进一个固定 Tick。 */
  step(): TickOutcome {
    // 同步 RNG/ID 的持久状态到工作实例（从 state 恢复以保证确定性）。
    this.rng = SeededRng.fromState(this.state.rng);
    this.ids = IdAllocator.fromState(this.state.ids);

    const tick = this.state.clock.tick;
    const ctx = new SimulationContext(this.state, tick, this.rng, this.ids);

    // 1. Consume Commands（按 clientSequence 稳定顺序）
    const commands = this.queue.drain();
    const commandResults: CommandResult[] = [];
    // 记录每个命令触发的事件在 pending 中的区间，用于填充 emittedEventIds。
    const commandEventSpans: Array<{ commandIndex: number; start: number; end: number }> = [];
    commands.forEach((command, commandIndex) => {
      const start = ctx.pendingCount;
      commandResults.push(this.applyCommand(ctx, command));
      commandEventSpans.push({ commandIndex, start, end: ctx.pendingCount });
    });

    // 2. Update Clock（在命令之后、系统之前推进，使本 Tick 系统看到新时间）
    this.state.clock.tick = tick + 1;
    this.state.clock.minuteOfDay += MINUTES_PER_TICK;
    if (this.state.clock.minuteOfDay >= MINUTES_PER_DAY) {
      this.state.clock.minuteOfDay -= MINUTES_PER_DAY;
      this.state.clock.day += 1;
    }

    // 3+. System Pipeline（固定注册顺序）
    for (const system of this.systems) {
      system.update(ctx);
    }

    // 编号并落事件缓冲（本 Tick 内 sequence 单调）
    this.eventBuffer.clear();
    const pending = ctx.drainPending();
    const recorded: RecordedEvent[] = [];
    for (let i = 0; i < pending.length; i++) {
      const p = pending[i];
      if (!p) continue;
      const ev: RecordedEvent = {
        eventId: this.ids.allocEventRecord(),
        type: p.type,
        tick: this.state.clock.tick,
        sequenceWithinTick: i,
        payload: p.payload,
        ...(p.causalCommandId !== undefined ? { causalCommandId: p.causalCommandId } : {}),
      };
      this.eventBuffer.append(ev);
      recorded.push(ev);
    }

    // 回填每个命令的 emittedEventIds（按命令触发区间映射到已编号事件）。
    for (const span of commandEventSpans) {
      const ids = recorded.slice(span.start, span.end).map((e) => e.eventId);
      const result = commandResults[span.commandIndex];
      if (result !== undefined) {
        commandResults[span.commandIndex] = { ...result, emittedEventIds: ids };
      }
    }

    // 将 RNG/ID 工作实例状态写回权威状态（可序列化）。
    this.state.rng = this.rng.snapshot();
    this.state.ids = this.ids.snapshot();

    // 不变量检查（开发/测试模式）
    const violations = this.invariants.checkAll(this.state);

    return {
      tick: this.state.clock.tick,
      commandResults,
      events: recorded,
      violations,
      stateHash: this.hashEveryTick ? hashWorldState(this.state) : null,
    };
  }

  private applyCommand(ctx: SimulationContext, command: GameCommand): CommandResult {
    const handler = this.handlers.get(command.type);
    let accepted = false;
    let rejectionCode: string | undefined;
    if (handler === undefined) {
      rejectionCode = 'COMMAND_UNKNOWN_TYPE';
    } else {
      const outcome = handler.apply(ctx, command.payload);
      accepted = outcome.accepted;
      rejectionCode = outcome.rejectionCode;
    }
    // emittedEventIds 在事件编号阶段按命令触发区间回填。
    return {
      commandId: command.commandId,
      accepted,
      emittedEventIds: [],
      ...(rejectionCode !== undefined ? { rejectionCode } : {}),
    };
  }
}
